import React, { useRef } from "react";
import Typical from "react-typical";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { HiOutlineArrowRight } from "react-icons/hi";
import { FaGithub, FaLinkedin } from "react-icons/fa";



const Hero = () => {
  const containerRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const tiltX = useTransform(my, [-0.5, 0.5], [6, -6]);
  const tiltY = useTransform(mx, [-0.5, 0.5], [-6, 6]);
  const sTiltX = useSpring(tiltX, { stiffness: 150, damping: 15 });
  const sTiltY = useSpring(tiltY, { stiffness: 150, damping: 15 });

  const handleMouse = (e) => {
    const r = containerRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const word = "Sanya Ololade";

  return (
    <div
      id="home"
      ref={containerRef}
      onMouseMove={handleMouse}
      className="relative min-h-[90vh] items-center justify-center flex w-[88%] lg:w-[82%] text-textClr mx-auto gap-12 lg:gap-20 dark:text-textClrDark flex-col lg:flex-row pt-8 pb-16"
    >
      <motion.div
        aria-hidden
        animate={{
          x: [0, 20, -10, 0],
          y: [0, -15, 10, 0],
          scale: [1, 1.08, 0.95, 1],
        }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -z-10 top-10 -left-10 w-[360px] h-[360px] rounded-full blur-3xl opacity-40 dark:opacity-30"
        style={{ background: "radial-gradient(circle, rgba(220,38,38,0.35), transparent 70%)" }}
      />
      <motion.div
        aria-hidden
        animate={{
          x: [0, -25, 15, 0],
          y: [0, 20, -10, 0],
          scale: [1, 1.1, 0.92, 1],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -z-10 bottom-0 right-0 w-[360px] h-[360px] rounded-full blur-3xl opacity-40 dark:opacity-30"
        style={{ background: "radial-gradient(circle, rgba(239,68,68,0.35), transparent 70%)" }}
      />

      <div className="lg:w-[55%] w-[95%] flex flex-col">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-sm lg:text-base opacity-70 font-light tracking-wide"
        >
          Software Engineer & Product Architect —
        </motion.span>

        <h1
          aria-label={word}
          className="font-semibold text-4xl md:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight mt-2"
        >
          {word.split("").map((ch, i) => (
            <motion.span
              key={i}
              initial={{ y: "110%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                delay: 0.1 + i * 0.03,
                duration: 0.6,
                ease: [0.2, 0.8, 0.2, 1],
              }}
              className="inline-block whitespace-pre"
            >
              {ch === " " ? "\u00A0" : ch}
            </motion.span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="text-accentClr dark:text-accentClrDark text-xl lg:text-3xl mt-5 font-medium"
        >
          <Typical
            loop={Infinity}
            steps={[
              "SaaS Platform Architect",
              3200,
              "Web3 & Blockchain Engineer",
              3200,
              "React Native Specialist",
              3200,
              "2× Hackathon Champion",
              3200,
              "Building Scalable Digital Products",
              3200,
            ]}
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="mt-6 max-w-xl text-[15px] lg:text-[17px] opacity-75 leading-relaxed"
        >
          Expert Full-stack Engineer specializing in high-performance web
          applications and decentralized systems. From building industrial-scale
          LMS platforms to winning notification systems for the Hyperliquid
          community, I bridge the gap between complex engineering and 
          pixel-perfect UX.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.5 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <a
            href="#projects"
            className="group relative inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold bg-accentClr dark:bg-accentClrDark text-white transition-all hover:brightness-110 shadow-lg shadow-accentClr/20"
          >
            View work
            <HiOutlineArrowRight className="transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="mailto:ololadetimileyin3@gmail.com"
            className="group relative inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold border border-textClr/10 dark:border-white/10 hover:border-accentClr dark:hover:border-accentClrDark transition-all"
          >
            Get in touch
          </a>
          <div className="flex items-center gap-6 ml-4">
            <motion.a
              whileHover={{ y: -2 }}
              href="https://github.com/LollyTim"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              className="opacity-50 hover:opacity-100 hover:text-accentClr dark:hover:text-accentClrDark transition-all"
            >
              <FaGithub size={22} />
            </motion.a>
            <motion.a
              whileHover={{ y: -2 }}
              href="https://www.linkedin.com/in/ololade-sanya-106990272/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="opacity-50 hover:opacity-100 hover:text-accentClr dark:hover:text-accentClrDark transition-all"
            >
              <FaLinkedin size={22} />
            </motion.a>
          </div>
        </motion.div>
      </div>

      <motion.div
        style={{ rotateX: sTiltX, rotateY: sTiltY, transformPerspective: 1000 }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
        className="lg:w-[40%] items-center flex justify-center"
      >
        <div className="relative">
          <motion.div
            aria-hidden
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            className="absolute -inset-6 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, rgba(220,38,38,0.4), transparent 30%, rgba(239,68,68,0.3) 60%, transparent 90%)",
              filter: "blur(24px)",
            }}
          />
          <div
            className="relative lg:w-[420px] lg:h-[420px] w-[280px] h-[280px] border border-accentClr dark:border-accentClrDark bg-center bg-cover rounded-full shadow-2xl"
            style={{ backgroundImage: `url("/LollyTech.jpeg")` }}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default Hero;
